import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { performUpdateInventoryMini } from "../../../../graphql/inventory/inventoryCRUD";
import { performUpdateOutboundMini } from "../../../../graphql/outbound/outboundCRUD";

import {
  Box,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { orange } from "@material-ui/core/colors";

import {
  Button,
  Confirm,
  Create,
  FormDataConsumer,
  maxValue,
  minValue,
  number,
  NumberInput,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  AddIcon,
  DAMAGED_COLOR,
  DamagedIcon,
  ExceptionIcon,
  RemoveIcon,
} from "../../../../components/common/iconography";
import { BackButton } from "../../../../components/common/BackButton";
import { ForceTitle } from "../../../../components/common/ForceTitle";
import { HelpButton } from "../../../../components/common/HelpButton";
import { LocationPicker } from "../../../../components/common/LocationPicker";
import { logError } from "../../../../utils/appUtils";
import {
  LocationLoadType,
  LocationType,
  NoteType,
  Unit,
} from "../../../../utils/constants";
import {
  isBulkLocationAvailable,
  isFloorLoaded,
  isLocationAvailable,
  setInventoryAisleList,
} from "../../../../utils/locationUtils";
import { getUnitName, isBulkShipment } from "../../../../utils/loadTypeUtils";
import {
  deepRedirect,
  getBackPath,
  trimDecimal,
} from "../../../../utils/utils";
import { InventoryItemHelp } from "./InventoryItemHelp";
import {
  allocateCapacity,
  ExceptionDialog,
  getInventoryItem,
  getInventoryItemStatus,
  handleAreaZoneLocation,
  isAllocatable,
  promiseCreateInventoryItem,
  promiseSetCapacityAllocated,
  shouldSplitUnitInventoryItemsAtLocation,
} from "./InventoryItemUtils";

const validateQtyRequired = (maxQty, maxCapacity) => [
  required(),
  number(),
  minValue(0.00001, "Must be greater than 0"),
  maxValue(maxCapacity, "Exceeds available capacity."),
  maxValue(maxQty, "Exceeds Qty. Available"),
];

// Form-level validation
const validateForm = (values, inventory) => {
  const errors = {};
  if (values.location?.type === LocationType.BULK) {
    const location = values.location;

    if (
      inventory &&
      !isBulkLocationAvailable(
        location,
        inventory,
        values.putawayQuantity,
        values.isDamaged,
      )
    ) {
      errors.location = "Location is un-available for storage.";
    }
  }
  return errors;
};

const CreateActions = (props) => {
  const [isExceptionDialogOpen, setIsExceptionDialogOpen] = useState();

  return (
    <TopToolbar>
      <HelpButton>
        <InventoryItemHelp {...props} />
      </HelpButton>
      <Button
        label="Exception"
        color="primary"
        onClick={() => setIsExceptionDialogOpen(true)}
      >
        <ExceptionIcon />
      </Button>
      <BackButton {...props} />
      <ExceptionDialog
        {...props}
        isExceptionDialogOpen={isExceptionDialogOpen}
        setIsExceptionDialogOpen={setIsExceptionDialogOpen}
      />
    </TopToolbar>
  );
};

const CreateToolbar = ({
  inventory,
  putawayQuantity,
  maxPutawayQty,
  locationInventoryItemMap,
  selectedLocation,
  locationType,
  exceptions,
  backPath,
  notify,
  ...props
}) => {
  const form = useForm();
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  // Outbound railcar the inventory is assigned to (set in transform, used in
  // onSuccess to sync the outbound shipment's totalUOM).
  const outboundShipmentIdRef = useRef(null);

  const handleValidateSave = () => {
    const formState = form.getState();
    const isValid = !formState.invalid; // Form-level validation status. Submit to force field-level form validation.
    let messages = [];
    debugger;

    if (isValid) {
      if (exceptions.overShipment && putawayQuantity <= maxPutawayQty) {
        messages.push("Overshipment Exception will be cleared.");
      } else if (!exceptions.overShipment && putawayQuantity > maxPutawayQty) {
        messages.push(
          "Putaway Qty. exceeds Qty. Available. Apply Overshipment Exception to confirm and Save.",
        );
      }
      if (inventory.underShipment && putawayQuantity >= maxPutawayQty) {
        messages.push(
          "Adding new Inventory Items will clear the Inventory Undershipment Exception.",
        );
      }

      if (messages.length) {
        messages.push("Proceed?");
        const messageFragment = (
          <>
            {messages.map((msg, index) => (
              <React.Fragment key={index}>
                {msg} <br />
              </React.Fragment>
            ))}
          </>
        );
        setMessage(messageFragment);
        setOpen(true);
      } else handleSave();
    } else handleSave();
  };

  const handleDialogClose = () => setOpen(false);

  const handleDialogConfirm = () => {
    if (putawayQuantity > maxPutawayQty && !exceptions.overShipment)
      handleDialogClose();
    else handleSave();
  };

  const handleSave = () => {
    debugger;
    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data };
    debugger;

    data.hasException = data.hasException ? data.hasException : false;
    data.isDamaged = data.hasException ? data.isDamaged : false;

    if (
      locationType === LocationType.STRUCTURED &&
      inventory.inventoryUOM === Unit.UNIT
    ) {
      // if STRUCTURED && UNIT, override data with first instance, spin off more in onSuccess
      for (let array of Object.values(locationInventoryItemMap)) {
        if (array.length > 0) {
          data = getInventoryItem(
            data,
            inventory,
            array[0].location,
            putawayQuantity,
            exceptions,
          );
          break;
        }
      }
    } else {
      // else if UNSTRUCTURED || STRUCTURED & OTHER || BULK, create single instance from data.
      data = getInventoryItem(
        data,
        inventory,
        data.location,
        putawayQuantity,
        exceptions,
      );
    }

    outboundShipmentIdRef.current = data.outboundShipmentId || null;

    delete data.aisle;
    delete data.area;
    delete data.inventory;
    delete data.location;
    delete data.outboundShipment;
    delete data.putawayQuantity;
    delete data.zone;

    return data;
  };

  const onSuccess = async ({ data }) => {
    try {
      debugger;
      data.putawayQuantity = putawayQuantity;

      let promiseList = [];
      let inventoryItemsArray = [];

      if (locationType === LocationType.STRUCTURED) {
        inventoryItemsArray = Object.values(locationInventoryItemMap).flat();

        // if inventoryUOM === UNIT, spin off STRUCTURED Inventory Items for each location, except the first one
        if (inventoryItemsArray.length > 1) {
          if (inventory.inventoryUOM === Unit.UNIT) {
            promiseList = inventoryItemsArray
              .slice(1)
              .map((inventoryItem) =>
                promiseCreateInventoryItem(inventoryItem),
              );
          } // else only 1 Inventory Item created in transform
        }

        // update location capacityAllocated for all Inventory Items.
        if (inventoryItemsArray.length > 0) {
          inventoryItemsArray.forEach((inventoryItem) => {
            if (!inventoryItem.location.locationLoad?.other.selected) {
              // if STRUCTURED & not OTHER, then update
              promiseList.push(
                promiseSetCapacityAllocated(
                  inventoryItem.location,
                  inventoryItem.location.capacityAllocated,
                ),
              );
            }
          });
        }
      } else {
        // if inventoryUOM === UNIT, spin off UNSTRUCTURED Inventory Items, all in the same aisle
        if (
          shouldSplitUnitInventoryItemsAtLocation({
            inventoryUOM: inventory.inventoryUOM,
            location: data.location,
            inventoryArea: inventory.area,
          })
        ) {
          inventoryItemsArray = Array.from({ length: putawayQuantity }, () =>
            getInventoryItem(data, inventory, data.location, 1, exceptions),
          );
          if (inventoryItemsArray.length > 1) {
            promiseList = inventoryItemsArray
              .slice(1)
              .map((inventoryItem) =>
                promiseCreateInventoryItem(inventoryItem),
              );
          }
        } else {
          // else only 1 UNSTRUCTURED || BULK InventoryItem created in transform
          inventoryItemsArray = [data];

          if (data.location.type === LocationType.BULK) {
            allocateCapacity(inventory, data);
            promiseList.push(
              promiseSetCapacityAllocated(
                data.location,
                data.location.capacityAllocated,
              ),
            );
          }
        }
      }

      const inventoryToUpdate = { ...inventory };
      if (inventory.inventoryItems?.items) {
        inventoryToUpdate.inventoryItems = [
          ...inventory.inventoryItems.items,
          ...inventoryItemsArray,
        ];
      } else {
        inventoryToUpdate.inventoryItems = inventoryItemsArray;
      }

      debugger;
      // check inventory status since we're assigning status to a new Inventory Item
      if (!isAllocatable(inventory)) {
        inventoryToUpdate.quantityReceived =
          inventoryToUpdate.inventoryItems.reduce((total, item) => {
            return total + item.quantityOnHand;
          }, 0);
      }

      setInventoryAisleList(inventoryToUpdate);

      inventoryToUpdate.history = [
        ...inventoryToUpdate.history,
        {
          type: NoteType.INVENTORY,
          date: data.createdAt,
          by: window.appUser?.fullName,
          text: `Allocated total of ${putawayQuantity} ${getUnitName(
            inventoryToUpdate.inventoryUOM,
          )}`,
        },
      ];

      // handle exceptions
      inventoryToUpdate.overShipment = exceptions.overShipmentQuantity > 0;
      inventoryToUpdate.overShipmentQuantity = exceptions.overShipmentQuantity;
      inventoryToUpdate.overShipmentNote = exceptions.overShipmentNote;

      inventoryToUpdate.history = [
        ...inventoryToUpdate.history,
        {
          type: NoteType.INVENTORY,
          date: data.createdAt,
          by: window.appUser?.fullName,
          text: `Overshipment of ${
            exceptions.overShipmentQuantity
          } ${getUnitName(inventoryToUpdate.inventoryUOM)}`,
        },
      ];

      if (inventory.underShipment && putawayQuantity >= maxPutawayQty) {
        inventoryToUpdate.underShipment = false;
        inventoryToUpdate.underShipmentQuantity = 0;
        inventoryToUpdate.underShipmentNote = "";
      }

      if (!inventory.isDamaged && exceptions.isDamaged)
        inventoryToUpdate.isDamaged = true;
      inventoryToUpdate.hasException =
        inventoryToUpdate.overShipment ||
        inventoryToUpdate.underShipment ||
        inventoryToUpdate.isDamaged ||
        inventory.isRecalled ||
        inventory.otherException;

      promiseList.push(performUpdateInventoryMini(inventoryToUpdate));

      // When the inventory is assigned to an outbound railcar, sync the
      // outbound shipment's totalUOM to the inventory's UOM.
      if (outboundShipmentIdRef.current) {
        promiseList.push(
          performUpdateOutboundMini({
            id: outboundShipmentIdRef.current,
            totalUOM: inventory.inventoryUOM,
          }),
        );
      }

      await Promise.all(promiseList);

      notify("Inventory Item created successfully", "success");
      deepRedirect(history, backPath);
    } catch (error) {
      logError("inventoryItems:InventoryItemCreate:onSuccess", error, notify);
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("inventoryItems:InventoryItemCreate:onFailure", error, notify);
  };

  const savingProps = { ...props, saving: isSaving };
  return (
    <Toolbar {...savingProps}>
      <SaveButton
        label={
          locationType === LocationType.STRUCTURED &&
          inventory.inventoryUOM === Unit.UNIT
            ? "Save All"
            : "Save"
        }
        submitOnEnter={false}
        undoable={false}
        disabled={!putawayQuantity || !selectedLocation}
        saving={isSaving}
        handleSubmitWithRedirect={handleValidateSave}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <Confirm
        isOpen={open}
        loading={isSaving}
        title="Save Confirmation"
        content={message}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </Toolbar>
  );
};

export const InventoryItemCreate = (props) => {
  const notify = useNotify();
  const [inventory] = useState(props.history?.location?.state?.inventory);
  const [locationType, setLocationType] = useState(LocationType.UNSTRUCTURED);
  const [putawayQuantity, setPutawayQuantity] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState();
  const [locationInventoryItemMap, setLocationInventoryItemMap] = useState({});
  const [exceptions, setExceptions] = useState({
    overShipment: inventory.overShipment,
    overShipmentQuantity: inventory.overShipmentQuantity,
    overShipmentNote: inventory.overShipmentNote,
  });
  const backPath = getBackPath(props);
  debugger;
  const initialValues = {
    groups: inventory.groups,
    status: getInventoryItemStatus(inventory),
    areaId: inventory.areaId,
    area: inventory.area,
    zoneId: inventory.zoneId,
    zone: inventory.zone,
    inventoryId: inventory.id,
    inventory: inventory,
    reloadYardId: inventory.reloadYardId,
    inboundShipmentId: inventory.inboundShipmentId,
    quantityCommitted: 0,
  };

  const initQtyAllocated = inventory.inventoryItems?.items.reduce(
    (total, item) => {
      return total + item.quantityOnHand;
    },
    0,
  );
  const maxPutawayQty =
    initQtyAllocated <= inventory.quantityAdvised
      ? trimDecimal(inventory.quantityAdvised - initQtyAllocated, 3)
      : 0;

  const updateExceptions = (
    newExceptions,
    newPutawayQuantity = putawayQuantity,
  ) => {
    // Calculate the new over shipment quantities based on the putawayQuantity
    if (newPutawayQuantity > maxPutawayQty) {
      newExceptions.overShipmentQuantity = trimDecimal(
        (inventory.overShipmentQuantity || 0) +
          newPutawayQuantity -
          maxPutawayQty,
        3,
      );
    } else {
      newExceptions.overShipmentQuantity = inventory.overShipmentQuantity || 0;
    }

    setExceptions(newExceptions);
  };

  return (
    <Create
      {...props}
      title={
        <ForceTitle
          title={
            isAllocatable(inventory)
              ? "Allocate Location"
              : "Create Inventory Item"
          }
        />
      }
      actions={
        <CreateActions
          {...props}
          backPath={backPath}
          inventory={inventory}
          exceptions={exceptions}
          setExceptions={updateExceptions}
        />
      }
    >
      <SimpleForm
        submitOnEnter={false}
        initialValues={initialValues}
        validate={(values) => validateForm(values, inventory)}
        toolbar={
          <CreateToolbar
            {...props}
            inventory={inventory}
            putawayQuantity={putawayQuantity}
            maxPutawayQty={maxPutawayQty}
            locationInventoryItemMap={locationInventoryItemMap}
            selectedLocation={selectedLocation}
            locationType={locationType}
            exceptions={exceptions}
            backPath={backPath}
            notify={notify}
          />
        }
      >
        <InventoryItemForm
          inventory={inventory}
          putawayQuantity={putawayQuantity}
          maxPutawayQty={maxPutawayQty}
          setPutawayQuantity={setPutawayQuantity}
          locationInventoryItemMap={locationInventoryItemMap}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          setLocationInventoryItemMap={setLocationInventoryItemMap}
          setLocationType={setLocationType}
          exceptions={exceptions}
          setExceptions={updateExceptions}
          notify={notify}
        />
      </SimpleForm>
    </Create>
  );
};

const getLocationHelperText = (form) => {
  if (form.getState().errors.location)
    return (
      <FormHelperText error>{form.getState().errors.location}</FormHelperText>
    );
  else return <FormHelperText>&nbsp;</FormHelperText>;
};

const InventoryItemForm = ({
  inventory,
  putawayQuantity,
  setPutawayQuantity,
  maxPutawayQty,
  locationInventoryItemMap,
  setLocationInventoryItemMap,
  selectedLocation,
  setSelectedLocation,
  setLocationType,
  exceptions,
  setExceptions,
  notify,
  ...props
}) => {
  const form = useForm();
  const [bayList, setBayList] = useState([]);
  const [maxCapacity, setMaxCapacity] = useState(Infinity);
  const inventoryLoadType =
    inventory.reloadYardCommodity?.commodity?.locationLoadType;
  const inventoryUOM = inventory.inventoryUOM;

  const isMaxQuantity = () => putawayQuantity === maxPutawayQty;
  const isMinQuantity = () => putawayQuantity === 0;

  // Subscribe to field state updates for 3rd party inputs
  form.registerField(
    "aisle",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "locationId",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "location",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );
  form.registerField(
    "outboundShipmentId",
    (fieldState) => {
      const { blur, change, focus, ...rest } = fieldState;
    },
    { active: true, dirty: true, touched: true, valid: true, value: true },
  );

  useEffect(() => {
    debugger;

    if (isBulkShipment(inventory.reloadYardCommodity?.commodity)) {
      if (!putawayQuantity) {
        form.change("putawayQuantity", maxPutawayQty);
        setPutawayQuantity(maxPutawayQty);
      }
      if (selectedLocation?.type === LocationType.BULK) {
        const availableCapacity =
          selectedLocation.bulkCapacity -
          ((selectedLocation.capacityUsed || 0) +
            (selectedLocation.capacityAllocated || 0));
        debugger;
        setMaxCapacity(availableCapacity);
      }
    }
    if (
      selectedLocation?.type === LocationType.STRUCTURED &&
      bayList.length > 0
    ) {
      const updatedBayList = [...bayList];
      for (let bay of updatedBayList) {
        for (let level of bay) {
          for (let loc of level) {
            if (loc.id === selectedLocation.id) {
              loc.capacityAllocated = selectedLocation.capacityAllocated;
              loc.capacityUsed = selectedLocation.capacityUsed;
            }
          }
        }
      }
      // Update the state with the new bayList
      setBayList(updatedBayList);
    }
  }, [selectedLocation]);

  useEffect(() => {
    // trigger form validation for putawayQuantity input when overShipment is changed.
    const putawayQuantity = form.getFieldState("putawayQuantity")?.value;
    if (putawayQuantity !== undefined && putawayQuantity !== null) {
      form.change("putawayQuantity", putawayQuantity - 1);
      form.change("putawayQuantity", putawayQuantity);
    }
  }, [exceptions.overShipment]);

  const handleLocation = (area, zone, aisle, location, outboundShipment) => {
    debugger;
    if (
      selectedLocation?.type !== location?.type &&
      location?.type === LocationType.STRUCTURED
    ) {
      let structuredAllocation = "";
      if (inventory.inventoryUOM === Unit.UNIT) {
        structuredAllocation = Object.values(locationInventoryItemMap).flat()
          .length;
      }
      form.change("putawayQuantity", structuredAllocation);
      setPutawayQuantity(structuredAllocation);
    }
    setLocationType(aisle?.type);
    setSelectedLocation(location);
    handleAreaZoneLocation(area, zone, aisle, location, outboundShipment, form);
  };

  const handlePutawayQuantity = (event) => {
    const newQtyAllocated = Number(event.target.value);
    setPutawayQuantity(newQtyAllocated);

    setExceptions(exceptions, newQtyAllocated);
  };

  const isAddDisabled = () => {
    if (!selectedLocation) return true;

    const isAvailable = isLocationAvailable(
      bayList,
      selectedLocation,
      inventory,
    );
    return !isAvailable;
  };

  const handleAddStructured = () => {
    const newIntentoryItem = getInventoryItem(
      null,
      inventory,
      selectedLocation,
      putawayQuantity,
      exceptions,
    );
    if (locationInventoryItemMap[selectedLocation.id]) {
      locationInventoryItemMap[selectedLocation.id] = [
        ...locationInventoryItemMap[selectedLocation.id],
        newIntentoryItem,
      ];
    } else {
      locationInventoryItemMap[selectedLocation.id] = [newIntentoryItem];
    }
    setLocationInventoryItemMap(locationInventoryItemMap);

    const newQtyAllocated = putawayQuantity + 1;
    setPutawayQuantity(newQtyAllocated);
    form.change("putawayQuantity", newQtyAllocated);

    setExceptions({}, newQtyAllocated);

    if (!selectedLocation.locationLoad[LocationLoadType.OTHER].selected) {
      const locationLoadType = selectedLocation.locationLoad[inventoryLoadType];
      selectedLocation.capacityAllocated =
        (selectedLocation.capacityAllocated || 0) + locationLoadType.capacity;
    }
    setSelectedLocation(selectedLocation);
  };

  const isRemoveDisabled = () => {
    if (!selectedLocation || !isLocationUsed(selectedLocation)) return true;

    if (isFloorLoaded(selectedLocation)) {
      debugger;
      const locationBay = bayList[selectedLocation.bay - 1];
      const isTopLevel = selectedLocation.level === locationBay.length;
      if (isTopLevel)
        return isMinQuantity() || selectedLocation.capacityAllocated === 0;

      const locationAbove =
        locationBay[selectedLocation.level][selectedLocation.position - 1];
      const quantityAbove =
        (locationAbove.capacityUsed || 0) +
        (locationAbove.capacityAllocated || 0);
      const quantityBelow =
        (selectedLocation.capacityUsed || 0) +
        (selectedLocation.capacityAllocated || 0);

      // cannot remove if above would have more than below
      if (quantityAbove >= quantityBelow) {
        return true;
      }
    }

    return isMinQuantity();
  };

  const isLocationUsed = (location) => {
    return locationInventoryItemMap[location.id]?.length > 0;
  };

  const handleRemoveStructured = () => {
    const locationItems = locationInventoryItemMap[selectedLocation.id];

    if (locationItems?.length > 0) {
      if (!selectedLocation.locationLoad[LocationLoadType.OTHER].selected) {
        const locationLoadType =
          selectedLocation.locationLoad[inventoryLoadType];
        selectedLocation.capacityAllocated =
          (selectedLocation.capacityAllocated || 0) - locationLoadType.capacity;
      }

      if (locationInventoryItemMap[selectedLocation.id]) {
        locationInventoryItemMap[selectedLocation.id].pop();
      }

      setSelectedLocation(selectedLocation);

      const newQtyAllocated = putawayQuantity - 1;
      setPutawayQuantity(newQtyAllocated);
      form.change("putawayQuantity", newQtyAllocated);

      setExceptions({}, newQtyAllocated);
    }
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextInput
                source="inventory.productName"
                label="Product Name"
                variant="outlined"
                fullWidth
                disabled={true}
                helperText={false}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                id="avaialble-quantity"
                label="Quantity Avaialble"
                variant="outlined"
                margin="dense"
                disabled={true}
                value={maxPutawayQty}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {" "}
                      {getUnitName(inventoryUOM)}{" "}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            {getPutawayQuantityInput(
              formData,
              inventory,
              maxPutawayQty,
              handlePutawayQuantity,
              maxCapacity,
              isAddDisabled,
              handleAddStructured,
              isRemoveDisabled,
              handleRemoveStructured,
              exceptions,
            )}
            {exceptions.overShipment && (
              <Grid container item xs={12} sm={3} spacing={1}>
                <Grid item xs={3}>
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Tooltip
                      arrow
                      placement="left"
                      title={
                        exceptions.overShipmentQuantity > 0
                          ? `Qty. Over: ${exceptions.overShipmentQuantity}`
                          : "Overshipment"
                      }
                    >
                      <ExceptionIcon
                        fontSize="large"
                        style={{ color: orange[600] }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box mt={2}>
                    <Typography variant="body2">
                      Overshipment
                      {exceptions.overShipmentQuantity > 0
                        ? `: ${exceptions.overShipmentQuantity}`
                        : ""}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            {exceptions.isDamaged && (
              <Grid container item xs={12} sm={3} spacing={1}>
                <Grid item xs={3}>
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Tooltip
                      title={
                        exceptions.originOfDamage
                          ? exceptions.originOfDamage
                          : "Damaged"
                      }
                      placement="left"
                      arrow
                    >
                      <DamagedIcon
                        fontSize="large"
                        style={{ color: DAMAGED_COLOR }}
                      />
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={9}>
                  <Box mt={2}>
                    <Typography variant="body2">
                      Damaged
                      {exceptions.originOfDamage
                        ? " by " + exceptions.originOfDamage
                        : ""}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Location:
              </Typography>
              <br />
            </Grid>
            <Grid item xs={12}>
              <LocationPicker
                {...props}
                inventory={inventory}
                inventoryItem={formData}
                originalLocation={null}
                isMaxQuantity={isMaxQuantity()}
                handleLocation={handleLocation}
                selectedLocation={selectedLocation}
                locationInventoryItemMap={locationInventoryItemMap}
                bayList={bayList}
                setBayList={setBayList}
                required={true}
              />
            </Grid>
            <Grid item xs={12}>
              {getLocationHelperText(form)}
            </Grid>
          </Grid>
        </>
      )}
    </FormDataConsumer>
  );
};

const getPutawayQuantityInput = (
  formData,
  inventory,
  maxPutawayQty,
  handlePutawayQuantity,
  maxCapacity,
  isAddDisabled,
  handleAddStructured,
  isRemoveDisabled,
  handleRemoveStructured,
  exceptions,
) => {
  if (isAllocatable(inventory)) {
    const aisle = formData.aisle;
    const isStructured = aisle?.type === LocationType.STRUCTURED;
    const inventoryUOM = inventory.inventoryUOM;
    const isQtyDisabled = isStructured && inventoryUOM === Unit.UNIT;
    const isAddRemoveVisible = isQtyDisabled;

    return (
      <Grid container item xs={12} sm={isAddRemoveVisible ? 6 : 3} spacing={1}>
        <Grid item xs={8} sm={isAddRemoveVisible ? 6 : 12}>
          <NumberInput
            source="putawayQuantity"
            label="Putaway Quantity"
            variant="outlined"
            fullWidth
            validate={
              exceptions.overShipment
                ? validateQtyRequired(Math.infinity, maxCapacity)
                : validateQtyRequired(maxPutawayQty, maxCapacity)
            }
            min={0}
            max={exceptions.overShipment ? Infinity : maxPutawayQty}
            disabled={isQtyDisabled}
            onChange={handlePutawayQuantity}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {" "}
                  {getUnitName(inventory.inventoryUOM)}{" "}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {isAddRemoveVisible && (
          <Grid item xs={4} sm={6}>
            <Box mt={1}>
              <Button
                label="ADD"
                color="default"
                size="large"
                disabled={isAddDisabled()}
                onClick={handleAddStructured}
              >
                <AddIcon />
              </Button>
              <Button
                label="REMOVE"
                color="default"
                size="large"
                disabled={isRemoveDisabled()}
                onClick={handleRemoveStructured}
              >
                <RemoveIcon />
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    );
  }

  return <></>;
};
